/** Kryspace derive-only render manifest (consumer contract; derivation lives in kryspace). */

export type RenderFieldAffordance = 'plain' | 'ref-chip' | 'select' | 'hidden';
export type RenderFieldTier = 'core' | 'context' | 'mechanical';
export type RenderSectionKind = 'rollup' | 'attention_queue' | 'completion_surface' | 'record';

export interface RenderManifestField {
  key: string;
  label: string;
  value: string | number | boolean | null;
  affordance: RenderFieldAffordance;
  tier: RenderFieldTier;
  readOnly: boolean;
}

export interface RenderManifestQueueItem {
  itemId: string;
  title: string;
  statusPhrase: string;
  priority?: string;
  recommended?: boolean;
  /**
   * Dual-plane aliases when the API emits both semantic and Postgres ids (SPA S7).
   * Prefer matching via ``itemIdPlane`` helpers; do not invent the missing plane.
   */
  itemIdAliases?: string[];
  postgresItemId?: string;
  semanticItemId?: string;
  kryspaceItemId?: string;
  /** Scope id for Control Bridge sidebar filter (artifact_instance_id). */
  domain?: string;
  filterScope?: string;
  /** Resource filter token (type bucket or person slug). */
  res?: string;
  assignedResourceRef?: string;
  assignedResourceLabel?: string;
  waypointRef?: string;
  activityTargetState?: string;
  issueType?: string;
  issueSummary?: string;
  /**
   * Enriched attention-queue fields (ExoPanel assigned-work closure WBS).
   * Backend/Kryspace may emit camelCase or snake_case; mapQueueItem normalizes.
   */
  activityDescription?: string;
  coordinationNoteRef?: string;
  blockedReason?: string;
  /** ExoPanel park (option #2) — mutually exclusive with blockedReason when set by backend. */
  deferReason?: string;
  dependsOnRefs?: string[];
  acceptanceHints?: string[];
  plankDescription?: string;
  futureCondition?: string;
  resourceAssignment?: string;
  /** Explicit “Go do work” SPA path or URL (optional; else triageRoute / heuristics). */
  workHref?: string;
  /** Agent vs Person pickup (FE-ARCH-5). */
  pickupKind?: string;
  /** Blocker class for FE-NAV click routing: none | warning | critical. */
  blockerClass?: string;
  /** Resource availability gate for Exo (FE-EXO-5). Default true when absent. */
  resourceAvailable?: boolean;
  /** Full Bridge Session required — divert to Chat (FE-EXO-7). */
  requiresBridgeSession?: boolean;
  /**
   * ExoPanel user-response vocab (U1 / F1). Prefer ``responseSchema`` (A6 derived).
   * ``userResponseType`` is a render hint mapped from that schema.
   */
  userResponseType?: string;
  userResponsePrompt?: string;
  userResponseOptions?: Array<{ label?: string; value?: string } | string>;
  userResponseTargetPropertyKeys?: string[];
  /** Derived response schema (A6) — unpopulated User-anchored properties on tracked artifact. */
  responseSchema?: Array<Record<string, unknown>>;
  responseSchemaTargetRef?: string;
  responseSchemaTargetState?: string;
  responseSchemaBlockedReason?: string;
  /**
   * Opgen A4 — tracked-record chrome for Exo (name / customer / value / age / last touch / owner).
   * Prefer over activityDescription when present.
   */
  recordContext?: {
    trackedArtifactRef?: string;
    name?: string;
    customer?: string;
    value?: string;
    ageAtStep?: string;
    lastTouch?: string;
    owner?: string;
  };
  /** ISO date (YYYY-MM-DD) when item entered current state — Gantt status-date anchor. */
  statusDate?: string;
  openImprovements?: number;
  improvementsTotal?: number;
  activeActivities?: number;
  activitiesTotal?: number;
  openIssues?: number;
  issuesTotal?: number;
  /** Waypoint ordering within a bridge session (Guidance / Mission Control). */
  sequenceOrder?: number;
  improvementRef?: string;
  improvementTitle?: string;
  assumptionText?: string;
  estimatedDuration?: string;
  startDate?: string;
  endDate?: string;
  duration?: string;
  gateMet?: boolean | null;
  timelineIndex?: number;
  dependsOnIndex?: number | null;
  workspaceSlug?: string;
  /** Engineering workspaces panel (render-manifest engineering-workspaces/v0.1) */
  engineeringWorkspaceSlug?: string;
  linkageMode?: string;
  reachable?: boolean;
  unreachableReason?: string;
  /** cross-domain-attention queue row */
  workspaceLabel?: string;
  contextKind?: 'personal' | 'company';
  triageRoute?: string;
  /** Diagnose scope health / VGR (render-manifest v0.1+) */
  correctionFactor?: number;
  vgrCorrectionFactor?: number;
  entropy?: number;
  valueGeneratedActual?: number;
  valueGeneratedIdeal?: number;
  costToGenerateActual?: number;
  costToGenerateIdeal?: number;
  vgrComponents?: RenderManifestVgrComponent[];
  /** Weekly VGR / entropy buckets for Diagnose subdomain expand chart */
  vgrHistory?: RenderManifestVgrHistoryPoint[];
  /** Mission Control domain VGR trio (render-manifest mc-domain-vgr) */
  vgrValue?: number;
  trendSeries?: Array<{ periodIndex?: number; value?: number }>;
  entropyClass?: string;
  entropyScore?: number;
  mostCriticalRank?: number;
}

export interface RenderManifestVgrHistoryPoint {
  periodLabel?: string;
  periodStart?: string;
  correctionFactor?: number;
  entropy?: number;
}

export interface RenderManifestVgrComponent {
  key: string;
  label: string;
  actual?: number;
  ideal?: number;
  displayActual?: string;
  displayIdeal?: string;
}

export interface RenderManifestSectionMeta {
  blockedActivityCount?: number;
  timingMode?: string;
  cascadeMode?: string;
  /** exopanel-calendar section — ISO date label for "today" chip */
  todayLabel?: string;
  /** control-bridge-personal-layer/v1 (phase 2 Guidance + Drive payloads) */
  schemaVersion?: string;
  source?: string;
  guidance?: unknown;
  drive?: unknown;
  /** cross-domain-attention/v0.1 (unified hub) */
  modeDefault?: 'capture' | 'respond' | 'log';
  capacityHeadline?: string;
  profileComplete?: boolean;
  /** DDD launch: pipeline hidden; glance limited to named domains. */
  launchSuppressions?: {
    pipeline?: string;
    domains?: string[];
  };
}

export interface RenderManifestSection {
  id: string;
  title: string;
  kind: RenderSectionKind;
  fields?: RenderManifestField[];
  queue?: RenderManifestQueueItem[];
  meta?: RenderManifestSectionMeta;
}

export interface KryspaceRenderManifest {
  schemaVersion: string;
  workspaceSlug: string;
  generatedAt: string;
  title: string;
  sections: RenderManifestSection[];
  readOnly: true;
}

export function isKryspaceRenderManifest(value: unknown): value is KryspaceRenderManifest {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const m = value as KryspaceRenderManifest;
  return (
    typeof m.schemaVersion === 'string' &&
    typeof m.workspaceSlug === 'string' &&
    typeof m.title === 'string' &&
    m.readOnly === true &&
    Array.isArray(m.sections)
  );
}
